import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../lib/axios';
import { API, useIsland, useLanguage, useTheme } from '../App';

/**
 * NewsTicker - A premium, auto-looping news ticker component
 * Features: Infinite scroll, pause on hover, touch swipe, multi-language, geo-targeting
 */
const NewsTicker = ({ showOn = 'homepage' }) => {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({
    enabled: true,
    speed: 'medium',
    position: 'top',
    theme: 'dark',
    gap_spacing: 40,
    show_on: ['homepage']
  });
  const [isPaused, setIsPaused] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const tickerRef = useRef(null);
  const contentRef = useRef(null);
  const animationRef = useRef(null);
  const touchStartX = useRef(0);
  const scrollPosition = useRef(0);
  const { selectedIsland } = useIsland();
  const { language } = useLanguage();
  const { theme: appTheme } = useTheme(); // Use app's theme for light/dark mode

  // Speed mapping (pixels per frame at 60fps)
  const speedMap = {
    slow: 0.5,
    medium: 1,
    fast: 2
  };

  // Fetch ticker data
  useEffect(() => {
    const fetchTickerData = async () => {
      try {
        const [itemsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/ticker/items`, {
            params: { island: selectedIsland?.toLowerCase() }
          }),
          axios.get(`${API}/ticker/settings`)
        ]);
        
        setItems(itemsRes.data.items || []);
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
        setIsLoaded(true);
        
        // Track impressions
        if (itemsRes.data.items?.length > 0) {
          itemsRes.data.items.forEach(item => {
            trackEvent(item.id, 'impression');
          });
        }
      } catch (error) {
        console.log('Ticker not available');
        setIsLoaded(true);
      }
    };
    
    fetchTickerData();
  }, [selectedIsland]);

  // Track events
  const trackEvent = async (itemId, eventType) => {
    try {
      const formData = new FormData();
      formData.append('item_id', itemId);
      formData.append('event_type', eventType);
      if (selectedIsland) {
        formData.append('island', selectedIsland.toLowerCase());
      }
      await axios.post(`${API}/ticker/track`, formData);
    } catch (error) {
      // Silent fail for analytics
    }
  };

  // Animation loop
  const animate = useCallback(() => {
    if (!contentRef.current || isPaused || items.length === 0) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    const speed = speedMap[settings.speed] || 1;
    scrollPosition.current += speed;
    
    const contentWidth = contentRef.current.scrollWidth / 2;
    
    if (scrollPosition.current >= contentWidth) {
      scrollPosition.current = 0;
    }
    
    contentRef.current.style.transform = `translateX(-${scrollPosition.current}px)`;
    animationRef.current = requestAnimationFrame(animate);
  }, [isPaused, settings.speed, items.length]);

  // Start animation
  useEffect(() => {
    if (items.length > 0 && settings.enabled) {
      animationRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, items.length, settings.enabled]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    if (!contentRef.current) return;
    const touchX = e.touches[0].clientX;
    const diff = touchStartX.current - touchX;
    scrollPosition.current += diff * 0.5;
    touchStartX.current = touchX;
  };

  const handleTouchEnd = () => {
    setIsPaused(false);
  };

  // Handle click
  const handleItemClick = (item) => {
    if (item.link_url) {
      trackEvent(item.id, 'click');
    }
  };

  // Don't render if disabled or no items
  if (!isLoaded || !settings.enabled || items.length === 0) {
    return null;
  }

  // Check if should show on current page
  if (!settings.show_on.includes('all') && !settings.show_on.includes(showOn)) {
    return null;
  }

  // Theme styles - Style C: Gradient Border Badges (uses app theme, not backend setting)
  const themeStyles = {
    dark: 'bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900',
    light: 'bg-gradient-to-r from-slate-100 via-white to-slate-100'
  };

  // Duplicate items for seamless loop
  const duplicatedItems = [...items, ...items];

  // Use app's theme (light/dark) instead of backend setting
  const isLightTheme = appTheme === 'light';

  return (
    <div
      ref={tickerRef}
      className={`w-full overflow-hidden ${themeStyles[appTheme] || themeStyles.dark} ${
        settings.position === 'bottom' ? 'fixed bottom-0 left-0 right-0 z-40' : 'relative z-40'
      }`}
      style={{ height: '80px' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Scrolling content */}
      <div
        ref={contentRef}
        className="flex items-center h-full whitespace-nowrap will-change-transform px-4 sm:px-6"
        style={{ gap: `${settings.gap_spacing}px` }}
      >
        {duplicatedItems.map((item, index) => (
          <TickerItem
            key={`${item.id}-${index}`}
            item={item}
            onClick={() => handleItemClick(item)}
            language={language}
            isLightTheme={isLightTheme}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Individual ticker item component - Style C: Gradient Border Badges
 */
const TickerItem = ({ item, onClick, language, isLightTheme }) => {
  // Check if item has content
  const hasTitle = item.title?.trim();
  const hasMessage = item.message?.trim();
  const hasEmoji = item.emoji;
  
  // Determine which logo to use based on theme
  // If light theme and logo_url_light exists, use it; otherwise fallback to logo_url
  const logoUrl = isLightTheme && item.logo_url_light 
    ? item.logo_url_light 
    : item.logo_url;
  const hasLogo = !!logoUrl;
  
  // Upgrade Cloudinary URL to higher quality/resolution
  const getHighResLogoUrl = (url) => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace(
      '/upload/',
      '/upload/h_512,c_fit,q_auto:best,f_auto/'
    );
  };
  
  // Fixed badge height - taller for better logo visibility
  const BADGE_HEIGHT = 'h-14 sm:h-16'; // 56px mobile, 64px desktop
  
  // Get max logo height based on item's logo_size setting - larger values
  const getLogoMaxHeight = () => {
    const sizes = {
      small: 'max-h-8 sm:max-h-10',
      medium: 'max-h-10 sm:max-h-12',
      large: 'max-h-12 sm:max-h-14',
      xlarge: 'max-h-14 sm:max-h-16'
    };
    return sizes[item.logo_size] || sizes.medium;
  };
  
  const content = (
    <div className="relative group flex-shrink-0">
      {/* Gradient border effect on hover */}
      <div className={`absolute -inset-[1px] bg-gradient-to-r from-primary/20 via-primary/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200`} />
      
      <div className={`relative flex items-center gap-2 sm:gap-3 px-3 sm:px-4 ${BADGE_HEIGHT} rounded-xl transition-all duration-200 ${
        isLightTheme 
          ? 'bg-white border border-slate-200 group-hover:border-primary/30' 
          : 'bg-slate-800/40 border border-slate-700/50 group-hover:border-primary/30'
      }`}>
        {/* Logo */}
        {hasLogo && (
          <img
            src={getHighResLogoUrl(logoUrl)}
            alt={item.title || ''}
            className={`object-contain flex-shrink-0 ${getLogoMaxHeight()} w-auto max-w-[120px] sm:max-w-[160px] ${
              isLightTheme 
                ? 'opacity-80 group-hover:opacity-100' 
                : 'opacity-90 group-hover:opacity-100'
            } transition-opacity`}
            loading="lazy"
          />
        )}
        
        {/* Emoji */}
        {hasEmoji && !hasLogo && (
          <span className="text-xl sm:text-2xl flex-shrink-0">{item.emoji}</span>
        )}
        
        {/* Divider dot - only if logo AND text content */}
        {(hasLogo || hasEmoji) && (hasTitle || hasMessage) && (
          <div className={`w-1 h-1 rounded-full flex-shrink-0 ${
            isLightTheme ? 'bg-slate-300' : 'bg-slate-600'
          }`} />
        )}
        
        {/* Title */}
        {hasTitle && (
          <span className={`text-sm sm:text-base font-medium whitespace-nowrap flex-shrink-0 ${
            isLightTheme ? 'text-slate-800' : 'text-white'
          }`}>
            {item.title}
          </span>
        )}
        
        {/* Separator - only if both title and message */}
        {hasTitle && hasMessage && (
          <span className={`flex-shrink-0 ${
            isLightTheme ? 'text-slate-400' : 'text-slate-500'
          }`}>—</span>
        )}
        
        {/* Message */}
        {hasMessage && (
          <span className={`text-sm sm:text-base whitespace-nowrap flex-shrink-0 ${
            isLightTheme ? 'text-slate-600' : 'text-white/80'
          }`}>
            {item.message}
          </span>
        )}
      </div>
    </div>
  );

  if (item.link_url) {
    return (
      <a
        href={item.link_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className="flex-shrink-0"
      >
        {content}
      </a>
    );
  }

  return <div className="flex-shrink-0">{content}</div>;
};

export default NewsTicker;
